import { marketExportLabel } from "@nexus/contracts";
import {
  assetToText as assetToTextFromExport,
  buildReportText as buildReportTextFromExport,
  formatNotes,
  humanizeEnum,
  singleAssetReport as singleAssetReportFromExport,
} from "@nexus/preflight-export";
import type {
  PreflightReportAsset,
  PreflightReportDetail,
  PreflightReportSummary,
} from "../types/preflight.types";
import { downloadText } from "./download";
import { featureDetails, featureDisplayState, visibleFeatures } from "./feature-format";

export { formatNotes, humanizeEnum };

const CSV_COLUMNS = [
  "Asset",
  "Platform",
  "Placement",
  "Market",
  "Asset Score",
  "Criterion",
  "Status",
  "Weight",
  "Notes",
] as const;

/** Em dash used for empty/not-applicable numeric cells so the column reads cleanly. */
const EMPTY_CELL = "—";

/** Human-readable labels for the raw feature-state enum (KTD5 five-state vocabulary). */
const STATE_LABELS: Record<string, string> = {
  PASS: "Pass",
  FAIL: "Fail",
  PARTIAL: "Partial",
  NOT_WEIGHTED: "Not weighted",
  NOT_APPLICABLE: "Not applicable",
  NOT_ANALYZED: "Not analyzed",
};

/** Feature states that contribute a weight to the score (everything else is unweighted). */
const WEIGHTED_STATES = new Set(["PASS", "FAIL", "PARTIAL"]);

/** Characters that make a spreadsheet treat a cell as a formula (CSV injection vector). */
const FORMULA_PREFIXES = ["=", "+", "-", "@"];

/** Quote a CSV cell and neutralize formula-injection on user-controlled values. */
function csvCell(value: string | number | null | undefined): string {
  const raw = value == null ? "" : String(value);
  const guarded = FORMULA_PREFIXES.some((p) => raw.startsWith(p))
    ? `'${raw}`
    : raw;
  return `"${guarded.replace(/"/g, '""')}"`;
}

export const EMPTY_CELL_TOKEN = EMPTY_CELL;

export function assetLabel(asset: PreflightReportAsset): string {
  return asset.originalName ?? asset.sourceUrl ?? asset.id;
}

export function humanizeState(state: string): string {
  return STATE_LABELS[state] ?? state;
}

/** Market for exports (R14) — shared with the server-rendered PDF so the two cannot drift. */
export function marketLabel(
  report: Pick<PreflightReportSummary, "market">,
): string {
  return marketExportLabel(report.market ?? null);
}

/** Turn a slug-ish brand handle ("first_aid_beauty") into a display name ("First Aid Beauty"). */
export function humanizeBrand(brand: string): string {
  return brand
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");
}

/**
 * Render a DS weight as a 2-decimal percentage ("12.50%"); blank when unweighted.
 * Matches the UI table and PDF (both toFixed(2)) so the weight column doesn't drift
 * across surfaces — whole-percent rounding turned 12.5% into a misleading "13%".
 */
export function formatWeight(weight: number, state: string): string {
  if (!WEIGHTED_STATES.has(state) || weight <= 0) return EMPTY_CELL;
  const pct = weight <= 1 ? weight * 100 : weight;
  return `${pct.toFixed(2)}%`;
}

export function formatScore(score: number | null): string {
  return score == null ? EMPTY_CELL : String(score);
}

/**
 * Build the CSV snapshot: standard header-first layout, one row per asset × criterion.
 * Columns: Asset, Platform, Placement, Market, Asset Score, Criterion, Status, Weight, Notes.
 * Platform, placement, and market are report-level and repeat on every row.
 * Excludes signed thumbnail URLs.
 */
export function buildReportCsv(report: PreflightReportDetail): string {
  const rows: string[] = [CSV_COLUMNS.map(csvCell).join(",")];
  const market = marketLabel(report);

  for (const asset of report.assets) {
    const label = assetLabel(asset);
    // Only the criteria users see (AD-1749) — hidden no-verdict criteria stay out of the CSV.
    const features = visibleFeatures(asset.features);
    if (features.length === 0) {
      rows.push(
        [
          csvCell(label),
          csvCell(report.platform),
          csvCell(report.placement),
          csvCell(market),
          csvCell(formatScore(asset.score)),
          csvCell(EMPTY_CELL),
          csvCell(humanizeState(asset.status)),
          csvCell(EMPTY_CELL),
          csvCell(asset.error ?? ""),
        ].join(","),
      );
      continue;
    }
    for (const feature of features) {
      rows.push(
        [
          csvCell(label),
          csvCell(report.platform),
          csvCell(report.placement),
          csvCell(market),
          csvCell(formatScore(asset.score)),
          csvCell(feature.name),
          // Display state so unweighted-but-scored criteria read as Pass/Fail, not "Not weighted".
          csvCell(humanizeState(featureDisplayState(feature))),
          csvCell(formatWeight(feature.weight, feature.state)),
          csvCell(formatNotes(featureDetails(feature))),
        ].join(","),
      );
    }
  }

  return rows.join("\r\n");
}

/**
 * Build the JSON snapshot of the report read model with ephemeral signed thumbnail URLs
 * stripped (a shared file must not leak live asset access — exports security requirement).
 */
export function buildReportJson(report: PreflightReportDetail): string {
  const sanitized: PreflightReportDetail = {
    ...report,
    assets: report.assets.map(({ thumbnailUrl: _thumbnailUrl, assetUrl: _assetUrl, ...asset }) => ({
      ...asset,
      assetUrl: null,
      thumbnailUrl: null,
      // Drop no-verdict criteria the UI hides (AD-1749); raw `state` is kept on the rest.
      features: visibleFeatures(asset.features),
    })),
  };
  return JSON.stringify(sanitized, null, 2);
}

/** Plain-text narrative — shared with the Public API so the Copy button cannot drift. */
export function buildReportText(report: PreflightReportDetail): string {
  return buildReportTextFromExport(report);
}

/**
 * Project the report down to a single asset, recomputing the report-level
 * aggregates (score, counts) so a per-asset export's summary reads correctly.
 */
export function singleAssetReport(
  report: PreflightReportDetail,
  asset: PreflightReportAsset,
): PreflightReportDetail {
  return singleAssetReportFromExport(report, asset);
}

/** Plain-text narrative for one asset — same content as the Text export, for copy-to-clipboard. */
export function assetToText(
  report: PreflightReportDetail,
  asset: PreflightReportAsset,
): string {
  return assetToTextFromExport(report, asset);
}

/** Lowercase, filesystem-safe slug from an arbitrary label ("Acme Co." → "acme-co"). */
function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Compose a stable, filesystem-safe export filename. */
export function exportFilename(
  report: Pick<PreflightReportDetail, "id" | "brandName">,
  ext: string,
): string {
  const slug = slugify(report.brandName);
  const date = new Date().toISOString().slice(0, 10);
  return `${slug || "report"}-${report.id}-${date}.${ext}`;
}

/** Per-asset export filename ("brand-asset-date.ext"); the asset slug keeps files distinct. */
export function assetExportFilename(
  report: Pick<PreflightReportDetail, "brandName">,
  asset: PreflightReportAsset,
  ext: string,
): string {
  const brand = slugify(report.brandName) || "report";
  const assetSlug = slugify(assetLabel(asset)) || slugify(asset.id) || "asset";
  const date = new Date().toISOString().slice(0, 10);
  return `${brand}-${assetSlug}-${date}.${ext}`;
}

/** Byte-order mark so Excel/Numbers detect UTF-8 and render accented brand names correctly. */
export const UTF8_BOM = "\uFEFF";

const CSV_MIME = "text/csv;charset=utf-8";
const JSON_MIME = "application/json";
const TEXT_MIME = "text/plain;charset=utf-8";

export function exportReportToCsv(report: PreflightReportDetail): void {
  downloadText(
    UTF8_BOM + buildReportCsv(report),
    exportFilename(report, "csv"),
    CSV_MIME,
  );
}

export function exportReportToJson(report: PreflightReportDetail): void {
  downloadText(buildReportJson(report), exportFilename(report, "json"), JSON_MIME);
}

export function exportReportToText(report: PreflightReportDetail): void {
  downloadText(buildReportText(report), exportFilename(report, "txt"), TEXT_MIME);
}

export function exportAssetToJson(
  report: PreflightReportDetail,
  asset: PreflightReportAsset,
): void {
  downloadText(
    buildReportJson(singleAssetReport(report, asset)),
    assetExportFilename(report, asset, "json"),
    JSON_MIME,
  );
}

export function exportAssetToText(
  report: PreflightReportDetail,
  asset: PreflightReportAsset,
): void {
  downloadText(
    buildReportText(singleAssetReport(report, asset)),
    assetExportFilename(report, asset, "txt"),
    TEXT_MIME,
  );
}

